import { Box, Chip, Tab, Tabs } from '@mui/material';
import React from 'react';
import { CATEGORY_VISUALS } from './notificationVisuals';

const CATEGORY_OPTIONS = Object.entries(CATEGORY_VISUALS).filter(([key]) => key !== 'outro');

const ABAS = Object.freeze([
  { valor: 'todas', rotulo: 'Todas', contador: 'total' },
  { valor: 'nao-lidas', rotulo: 'Não lidas', contador: 'naoLidas' },
  { valor: 'lidas', rotulo: 'Lidas', contador: 'lidas' },
]);

/**
 * Abas de status + chips de categoria da área de notificações.
 *
 * A seleção não é estado local: quem consome passa a aba/categoria persistidas
 * no backend (`NotificacaoEstado`) e recebe as mudanças por callback. Assim a
 * gaveta do sino e a central mostram exatamente a mesma janela.
 *
 * @param {{
 *   aba?: 'todas'|'nao-lidas'|'lidas',
 *   categoria?: string,
 *   counts?: {total?: number, naoLidas?: number, lidas?: number},
 *   countsPorCategoria?: Record<string, {total?: number}>,
 *   onAbaChange?: (aba: string) => void,
 *   onCategoriaChange?: (categoria: string) => void,
 *   variant?: 'page'|'drawer',
 *   sx?: object,
 * }} props
 */
const NotificationFilters = ({
  aba = 'todas',
  categoria = '',
  counts = {},
  countsPorCategoria = {},
  onAbaChange,
  onCategoriaChange,
  variant = 'page',
  sx,
}) => {
  const naGaveta = variant === 'drawer';
  const rotuloTodasCategorias = naGaveta ? 'Todas' : 'Todas as categorias';

  return (
    <>
      <Tabs
        value={aba}
        onChange={(_event, valor) => onAbaChange?.(valor)}
        variant={naGaveta ? 'fullWidth' : 'scrollable'}
        allowScrollButtonsMobile={!naGaveta}
        aria-label="Filtrar notificações por status"
        sx={{
          px: naGaveta ? 0 : 1,
          minHeight: naGaveta ? 42 : undefined,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {ABAS.map((item) => {
          const contador = Number(counts[item.contador]) || 0;

          return (
            <Tab
              key={item.valor}
              value={item.valor}
              label={`${item.rotulo} (${contador})`}
              sx={naGaveta ? { textTransform: 'none', minHeight: 42 } : undefined}
            />
          );
        })}
      </Tabs>

      <Box
        role="group"
        data-no-swipe={naGaveta ? true : undefined}
        data-testid={naGaveta ? 'notificacoes-categorias' : undefined}
        aria-label="Filtrar notificações por categoria"
        sx={{
          display: 'flex',
          gap: 0.75,
          px: naGaveta ? 1.5 : 2,
          py: naGaveta ? 1 : 1.5,
          overflowX: 'auto',
          borderBottom: '1px solid',
          borderColor: 'divider',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          ...sx,
        }}
      >
        <Chip
          size="small"
          label={rotuloTodasCategorias}
          color={categoria ? 'default' : 'primary'}
          variant={categoria ? 'outlined' : 'filled'}
          onClick={() => onCategoriaChange?.('')}
          sx={{ flexShrink: 0 }}
        />
        {CATEGORY_OPTIONS.map(([key, visual]) => {
          const Icon = visual.Icon;
          const contadorCategoria = Number(countsPorCategoria?.[key]?.total) || 0;

          return (
            <Chip
              key={key}
              size="small"
              icon={<Icon />}
              label={
                contadorCategoria > 0 ? `${visual.label} (${contadorCategoria})` : visual.label
              }
              color={categoria === key ? 'primary' : 'default'}
              variant={categoria === key ? 'filled' : 'outlined'}
              onClick={() => onCategoriaChange?.(key)}
              sx={{ flexShrink: 0 }}
            />
          );
        })}
      </Box>
    </>
  );
};

export default React.memo(NotificationFilters);
